
<!-- src/components/ForecastWeekInput.vue -->
<template>
  <div class="card core-card core-section">
    <div class="card-content">
      <div class="forecast-heading">
        <span class="card-title">
          Week Ending: {{ formatUK(week.dates[6]) }}
        </span>

        <span v-if="week.aiGenerated" class="ai-label">
          <i class="material-icons tiny">auto_awesome</i>
          AI Prepared
        </span>
      </div>

      <div
        v-if="lockMessage"
        class="core-feedback core-feedback--error"
      >
        {{ lockMessage }}
      </div>

      <div v-if="week.tradingPatternMessage" class="trading-note">
        <i class="material-icons tiny">event_available</i>
        {{ week.tradingPatternMessage }}
      </div>

      <div v-if="week.aiGenerated" class="ai-information">
        <i class="material-icons tiny">insights</i>
        <span>
          Forecast calculated using available historical
          trading data and the venue's Audit trading pattern.
          Review the figures before submitting.
        </span>
      </div>

      <!-- DESKTOP TABLE -->
      <div
        class="responsive-table hide-on-small-only"
        style="margin-top: 10px"
      >
        <table class="highlight core-table forecast-table">
          <thead>
            <tr>
              <th>Category</th>

              <th v-for="(day, index) in days" :key="'h-' + index">
                <span class="day-name">{{ day }}</span>
                <br />
                <span class="day-date grey-text text-darken-1">
                  {{ formatUK(week.dates[index]) }}
                </span>

                <div
                  v-if="isClosed(index)"
                  class="closed-heading"
                >
                  CLOSED
                </div>
              </th>

              <th>Total for Week</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td class="core-strong">
                Total (for each day)
              </td>

              <td
                v-for="(total, index) in dayTotals"
                :key="'dt-' + index"
                class="core-strong"
                :class="{ 'closed-cell': isClosed(index) }"
              >
                <span v-if="isClosed(index)">Closed</span>
                <span v-else>{{ formatGBP(total) }}</span>
              </td>

              <td class="core-strong">
                {{ formatGBP(weekTotalAll) }}
              </td>
            </tr>

            <tr v-for="category in categories" :key="category">
              <td class="cat-name">
                {{ labelForCategory(category) }}
              </td>

              <td
                v-for="(_, index) in days"
                :key="category + '-' + index"
                :class="{ 'closed-cell': isClosed(index) }"
              >
                <span v-if="isClosed(index)" class="closed-value">
                  Closed
                </span>

                <input
                  v-else
                  type="number"
                  step="1"
                  min="0"
                  class="browser-default forecast-input"
                  :disabled="!editable || busy"
                  :value="week.rows[category][index]"
                  @change="
                    onInput(
                      category,
                      index,
                      $event.target.value
                    )
                  "
                />
              </td>

              <td class="cat-week-total">
                {{ formatGBP(rowTotal(category)) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- MOBILE TABLE -->
      <div
        class="show-on-small hide-on-med-and-up"
        style="margin-top: 10px"
      >
        <div class="responsive-table">
          <table class="highlight core-table mobile-forecast">
            <thead>
              <tr>
                <th>Category</th>
                <th>Total</th>
                <th>Liquor</th>
                <th>Food</th>
                <th>Door</th>
                <th>Other</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(day, index) in days"
                :key="'mday-' + index"
                :class="{ 'closed-row': isClosed(index) }"
              >
                <td class="mobile-day">
                  <div class="mobile-day-name">
                    {{ day }}
                  </div>

                  <div class="mobile-day-date grey-text text-darken-1">
                    {{ formatUK(week.dates[index]) }}
                  </div>
                </td>

                <template v-if="isClosed(index)">
                  <td class="closed-value">Closed</td>

                  <td
                    v-for="category in categories"
                    :key="'closed-' + category"
                    class="closed-value"
                  >
                    —
                  </td>
                </template>

                <template v-else>
                  <td class="core-strong">
                    {{ formatGBP(dayTotals[index]) }}
                  </td>

                  <td
                    v-for="category in categories"
                    :key="category + '-' + index"
                  >
                    <input
                      type="number"
                      step="1"
                      min="0"
                      class="browser-default forecast-input forecast-input--mobile"
                      :disabled="!editable || busy"
                      :value="week.rows[category][index]"
                      @change="
                        onInput(
                          category,
                          index,
                          $event.target.value
                        )
                      "
                    />
                  </td>
                </template>
              </tr>

              <tr>
                <td class="core-strong">Total for week</td>

                <td class="core-strong">
                  {{ formatGBP(weekTotalAll) }}
                </td>

                <td
                  v-for="category in categories"
                  :key="'total-' + category"
                  class="core-strong"
                >
                  {{ formatGBP(rowTotal(category)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- EXISTING CONTROLS -->
      <div class="core-actions forecast-actions">
        <button
          class="btn"
          :disabled="!canSubmit || busy"
          @click="onSubmit"
        >
          Submit week
        </button>

        <button
          class="btn grey"
          :disabled="!canAdjust || busy"
          @click="onAdjust"
        >
          Adjust forecast
        </button>

        <button
          v-if="editable && submitted"
          class="btn red lighten-1"
          :disabled="busy"
          @click="onStopAdjust"
        >
          Stop adjusting
        </button>

        <span
          v-if="submitted"
          class="core-badge core-badge--success"
        >
          Submitted
        </span>
      </div>

      <div v-if="editable" class="forecast-ai-actions">
        <button
          type="button"
          class="btn-flat teal-text text-darken-2"
          :disabled="busy"
          @click="emit('regenerate')"
        >
          <i class="material-icons left">auto_awesome</i>
          Regenerate AI Forecast
        </button>

        <span class="grey-text ai-help">
          Replaces existing figures only after confirmation.
          Closed days remain excluded.
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  week: { type: Object, required: true },
  categories: { type: Array, required: true },
  days: { type: Array, required: true },
  formatUK: { type: Function, required: true },
  editable: { type: Boolean, required: true },
  submitted: { type: Boolean, required: true },
  canSubmit: { type: Boolean, required: true },
  canAdjust: { type: Boolean, required: true },
  busy: { type: Boolean, default: false },
  lockMessage: { type: String, default: "" },
});

const emit = defineEmits([
  "cell",
  "submit",
  "adjust",
  "stop-adjust",
  "regenerate",
]);

const CATEGORY_LABELS = {
  liquor: "Liquor",
  food: "Food",
  door: "Door",
  other: "Other",
};

function labelForCategory(category) {
  return CATEGORY_LABELS[category] || category;
}

function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(value) {
  return asNumber(value).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function isClosed(dayIndex) {
  // Submitted weeks display their saved figures unchanged.
  if (props.submitted) return false;

  return props.week?.closedDays?.[dayIndex] === true;
}

function onInput(category, dayIndex, raw) {
  if (
    !props.editable ||
    props.busy ||
    isClosed(dayIndex)
  ) return;

  emit("cell", {
    category,
    dayIndex,
    value: Math.max(0, asNumber(raw)),
  });
}

function rowTotal(category) {
  const values = props.week?.rows?.[category] || [];

  return values.reduce(
    (sum, value) => sum + asNumber(value),
    0
  );
}

const dayTotals = computed(() => {
  const totals = Array(7).fill(0);

  for (const category of props.categories) {
    const values = props.week?.rows?.[category] || [];

    for (let index = 0; index < 7; index++) {
      totals[index] += asNumber(values[index]);
    }
  }

  return totals;
});

const weekTotalAll = computed(() =>
  dayTotals.value.reduce(
    (sum, value) => sum + asNumber(value),
    0
  )
);

function onSubmit() {
  if (!props.busy && props.canSubmit) {
    emit("submit");
  }
}

function onAdjust() {
  if (!props.busy && props.canAdjust) {
    emit("adjust");
  }
}

function onStopAdjust() {
  if (!props.busy) {
    emit("stop-adjust");
  }
}
</script>

<style scoped>
.forecast-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.ai-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 5px;
  background: #e0f2f1;
  color: #00695c;
  font-size: 12px;
  font-weight: 600;
}

.trading-note,
.ai-information {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.5;
}

.trading-note {
  background: #f5f5f5;
  color: #455a64;
  border-left: 3px solid #90a4ae;
}

.ai-information {
  background: #f0f7f6;
  color: #37474f;
  border-left: 3px solid #00897b;
}

.forecast-table,
.mobile-forecast {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.forecast-table th,
.forecast-table td,
.mobile-forecast th,
.mobile-forecast td {
  vertical-align: middle;
  white-space: normal;
  word-break: break-word;
}

.cat-name,
.cat-week-total,
.day-name {
  font-weight: 700;
}

.day-date {
  font-size: 12px;
}

.forecast-input {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
  padding: 2px 6px;
}

.closed-heading {
  color: #b71c1c;
  font-size: 11px;
  font-weight: 700;
  margin-top: 5px;
}

.closed-cell,
.closed-row {
  background: #f5f5f5;
}

.closed-value {
  color: #78909c;
  font-size: 12px;
  font-weight: 600;
}

.mobile-forecast th,
.mobile-forecast td {
  padding: 8px 6px;
  font-size: 13px;
}

.mobile-day-name {
  font-weight: 700;
}

.mobile-day-date {
  font-size: 11px;
  line-height: 1.2;
}

.forecast-input--mobile {
  width: 100% !important;
  padding: 2px 4px;
  box-sizing: border-box;
  font-size: 13px;
}

.forecast-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.forecast-ai-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.ai-help {
  font-size: 12px;
}

@media (min-width: 601px) and (max-width: 992px) {
  .forecast-table th,
  .forecast-table td {
    padding: 6px;
    font-size: 13px;
  }

  .forecast-input {
    padding: 2px 4px;
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .mobile-forecast {
    min-width: 620px;
  }

  .responsive-table {
    margin-left: -2px;
    margin-right: -2px;
  }

  .forecast-ai-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
